import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ChevronRight, Download, Layers, RefreshCw, Search, Upload } from "lucide-react";

const FILTERS = [
  { k: "all", l: "Tutti" }, { k: "new", l: "Nuovi" }, { k: "existing", l: "Esistenti" },
  { k: "duplicates", l: "Duplicati CSV" }, { k: "derived", l: "Ragione sociale ricavata" },
  { k: "invalid", l: "Denominazione mancante" }, { k: "vat10", l: "Partita IVA 10 cifre" },
  { k: "invalid_email", l: "Email non valida" }, { k: "invalid_pec", l: "PEC non valida" },
  { k: "internal", l: "Anagrafiche interne" }, { k: "blocked", l: "Bloccati" },
];

export default function CustomerImport() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [fileObj, setFileObj] = useState(null);
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [simStats, setSimStats] = useState(null);
  const [report, setReport] = useState(null);

  const upload = async () => {
    if (!fileObj) return toast.error("Seleziona un file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", fileObj); fd.append("delimiter", ";");
      const r = await api.post("/customers/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSession(r.data); setEntries(r.data.entries || []); setStep(1);
      toast.success(`File analizzato: ${r.data.row_count} righe`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore lettura file"); }
    finally { setBusy(false); }
  };

  const applyVatFix = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/customers/import/sessions/${session.session_id}/apply-vat-fix`, { apply_leading_zero: true });
      toast.success(`Zero iniziale applicato a ${r.data.changed} Partite IVA`);
      // reload session
      const s = await api.post("/customers/import/analyze", (() => { const fd = new FormData(); fd.append("file", fileObj); fd.append("delimiter", ";"); return fd; })(), { headers: { "Content-Type": "multipart/form-data" } }).catch(() => null);
      if (s) { setSession(s.data); setEntries(s.data.entries || []); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const patchEntry = async (row, patch) => {
    setEntries((prev) => prev.map((e) => e.row_id === row.row_id ? { ...e, ...patch } : e));
    try { await api.patch(`/customers/import/sessions/${session.session_id}/entries/${row.row_id}`, patch); }
    catch { toast.error("Errore salvataggio modifica"); }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/customers/import/sessions/${session.session_id}/simulate`);
      setSimStats(r.data.stats); setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!window.confirm(`Confermi la creazione di ${simStats?.new || 0} nuovi clienti e l'aggiornamento di ${simStats?.to_update || 0}?`)) return;
    setBusy(true);
    try {
      const key = `${session.session_id}-${Date.now()}`;
      const r = await api.post(`/customers/import/sessions/${session.session_id}/confirm`, { idempotency_key: key });
      setReport(r.data); setStep(3);
      toast.success("Importazione completata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    let list = entries;
    if (filter === "new") list = list.filter((e) => e.product_status === "new");
    else if (filter === "existing") list = list.filter((e) => e.product_status === "existing");
    else if (filter === "duplicates") list = list.filter((e) => e.product_status === "csv_duplicate");
    else if (filter === "derived") list = list.filter((e) => e.flags?.derived_company);
    else if (filter === "invalid") list = list.filter((e) => e.warnings?.includes("Denominazione cliente mancante"));
    else if (filter === "vat10") list = list.filter((e) => e.flags?.vat_status === "10_digits");
    else if (filter === "invalid_email") list = list.filter((e) => e.warnings?.includes("Email non valida"));
    else if (filter === "invalid_pec") list = list.filter((e) => e.warnings?.includes("PEC non valida"));
    else if (filter === "internal") list = list.filter((e) => e.flags?.internal);
    else if (filter === "blocked") list = list.filter((e) => e.action === "skip");
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((e) => (e.company_name + " " + (e.vat_number || "") + " " + (e.email || "") + " " + (e.city || "")).toLowerCase().includes(s));
    }
    return list.slice(0, 500);
  }, [entries, filter, q]);

  const vat10Count = entries.filter(e => e.flags?.vat_status === "10_digits").length;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-4" data-testid="customer-import-page">
      <Link to="/clienti" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Clienti</Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Clienti</div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">Importazione anagrafiche</h1>
        <p className="text-sm text-slate-500">Fonte consigliata: <b>CSV clienti AMA / eBridge Buffetti</b> (UTF-8, ; delimitato, 12 colonne).</p>
      </div>
      <Alert className="bg-amber-50 border-amber-200 text-amber-800">
        <AlertTitle>Ambiente di anteprima</AlertTitle>
        <AlertDescription>Utilizzare esclusivamente file di prova o dati anonimizzati. Eseguire l'importazione definitiva nell'ambiente di produzione.</AlertDescription>
      </Alert>

      <div className="flex items-center gap-2 text-xs text-slate-600 flex-wrap">
        {[{n:"Carica",i:0},{n:"Anteprima",i:1},{n:"Simulazione",i:2},{n:"Rapporto",i:3}].map((s,idx,arr)=>(
          <div key={s.n} className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full font-semibold ${step >= s.i ? "bg-[#0a0a2e] text-white" : "bg-slate-100"}`}>{idx+1}. {s.n}</div>
            {idx < arr.length-1 && <ChevronRight className="w-3 h-3 text-slate-400" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card className="ama-card border-0 shadow-none">
          <CardHeader><CardTitle className="font-heading text-lg">Carica il CSV clienti</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>File CSV</Label>
              <Input data-testid="ci-file" type="file" accept=".csv,.txt" onChange={(e)=>setFileObj(e.target.files?.[0]||null)} />
            </div>
            <div className="text-xs text-slate-500">Attese 12 colonne: EMAIL, NOME, COGNOME, VIA, CITTA, CAP, TELEFONO, CODICE FISCALE, PARTITA IVA, RAGIONE SOCIALE, PEC, CODICE SDI. Il codice cliente verrà generato automaticamente.</div>
            <div className="flex justify-end">
              <Button data-testid="ci-upload" onClick={upload} disabled={busy || !fileObj} className="ama-btn-primary">
                {busy ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Analisi…</> : <><Upload className="w-4 h-4 mr-1" /> Analizza file</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <>
          <Card className="ama-card border-0 shadow-none">
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <Stat k="File" v={session?.source_filename} />
              <Stat k="Righe" v={session?.row_count} />
              <Stat k="Codici da generare" v={session?.code_range?.count || 0} />
              <Stat k="Primo codice" v={session?.code_range?.first || "—"} />
              <Stat k="Ultimo codice" v={session?.code_range?.last || "—"} />
            </CardContent>
          </Card>

          {vat10Count > 0 && (
            <Alert className="bg-blue-50 border-blue-200 text-blue-900">
              <AlertTitle>Correzione Partita IVA proposta</AlertTitle>
              <AlertDescription>
                Trovate {vat10Count} Partite IVA a 10 cifre. Puoi applicare in blocco lo zero iniziale mancante prima di procedere.
                <div className="mt-2"><Button size="sm" data-testid="ci-vat-fix" onClick={applyVatFix} disabled={busy} variant="outline">Applica zero iniziale alle {vat10Count} Partite IVA</Button></div>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap gap-1 items-center">
            {FILTERS.map((f)=>(
              <button key={f.k} data-testid={`ci-filter-${f.k}`} onClick={()=>setFilter(f.k)} className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${filter === f.k ? "bg-[#0a0a2e] text-white border-transparent" : "bg-white text-slate-700 border-slate-200"}`}>{f.l}</button>
            ))}
            <div className="relative ml-auto">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 w-56" placeholder="Cerca…" value={q} onChange={(e)=>setQ(e.target.value)} />
            </div>
          </div>

          <div className="hidden md:block overflow-x-auto ama-card">
            <table className="w-full text-xs">
              <thead className="uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left p-2">Riga</th>
                  <th className="text-left p-2">Cod. proposto</th>
                  <th className="text-left p-2">Ragione sociale</th>
                  <th className="text-left p-2">P.IVA</th>
                  <th className="text-left p-2">C.F.</th>
                  <th className="text-left p-2">Nome</th>
                  <th className="text-left p-2">Cognome</th>
                  <th className="text-left p-2">PEC</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Via</th>
                  <th className="text-left p-2">Città</th>
                  <th className="text-left p-2">CAP</th>
                  <th className="text-left p-2">SDI</th>
                  <th className="text-left p-2">Tel</th>
                  <th className="text-left p-2">Stato</th>
                  <th className="text-left p-2">Azione</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.row_id} className="border-t border-slate-100" data-testid={`ci-row-${e.row_id}`}>
                    <td className="p-2 text-slate-400">{e.source_row_number}</td>
                    <td className="p-2 font-mono">{e.proposed_customer_code || (e.existing_customer_id ? <Badge variant="outline" className="text-[9px]">esistente</Badge> : "—")}</td>
                    <td className="p-2 font-semibold">{e.company_name || <em className="text-rose-500">mancante</em>}</td>
                    <td className="p-2 font-mono">{e.vat_number}{e.flags?.vat_status === "10_digits" && <div className="text-[10px] text-amber-700">→ 0{e.vat_number}</div>}</td>
                    <td className="p-2 font-mono">{e.tax_code}</td>
                    <td className="p-2">{e.first_name}</td>
                    <td className="p-2">{e.last_name}</td>
                    <td className="p-2 truncate max-w-[120px]">{e.certified_email}</td>
                    <td className="p-2 truncate max-w-[120px]">{e.email}</td>
                    <td className="p-2 truncate max-w-[140px]">{e.address}</td>
                    <td className="p-2">{e.city}</td>
                    <td className="p-2 font-mono">{e.postal_code}</td>
                    <td className="p-2 font-mono">{e.sdi_code}</td>
                    <td className="p-2 font-mono">{e.phone}</td>
                    <td className="p-2"><Badge variant="outline" className="text-[9px]">{e.product_status}</Badge></td>
                    <td className="p-2">
                      <select value={e.action} onChange={(ev)=>patchEntry(e, { action: ev.target.value })} className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white">
                        <option value="create">Crea</option><option value="update_all">Aggiorna</option>
                        <option value="keep">Mantieni</option><option value="skip">Ignora</option><option value="review">Rivedi</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="p-8 text-center text-slate-500">Nessun risultato.</div>}
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {filtered.map((e)=>(
              <div key={e.row_id} className="ama-card p-3 space-y-0.5 text-sm">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="font-mono text-xs">{e.proposed_customer_code || "—"}</span>
                  <Badge variant="outline" className="text-[9px]">{e.product_status}</Badge>
                  <span className="font-semibold flex-1">{e.company_name || <em className="text-rose-500">Denominazione mancante</em>}</span>
                </div>
                <div className="text-xs">P.IVA: {e.vat_number || "—"} · CF: {e.tax_code || "—"}</div>
                <div className="text-xs">{e.first_name} {e.last_name}</div>
                <div className="text-xs truncate">PEC: {e.certified_email || "—"} · Email: {e.email || "—"}</div>
                <div className="text-xs">{e.address} · {e.city} · {e.postal_code}</div>
                <div className="text-xs">SDI: {e.sdi_code || "—"} · Tel: {e.phone || "—"}</div>
                {e.warnings?.length > 0 && <div className="text-[10px] text-amber-700">⚠️ {e.warnings.join(" · ")}</div>}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={()=>setStep(0)}>Annulla</Button>
            <Button data-testid="ci-simulate" onClick={simulate} disabled={busy} className="ama-btn-primary"><Layers className="w-4 h-4 mr-1" /> Simula importazione</Button>
          </div>
        </>
      )}

      {step === 2 && simStats && (
        <Card className="ama-card border-0 shadow-none" data-testid="ci-sim">
          <CardHeader><CardTitle className="font-heading text-lg">Rapporto di simulazione</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <Stat k="Totale righe" v={simStats.total} />
            <Stat k="Nuovi clienti" v={simStats.new} />
            <Stat k="Esistenti" v={simStats.existing} />
            <Stat k="Da aggiornare" v={simStats.to_update} />
            <Stat k="Duplicati CSV" v={simStats.csv_duplicate} />
            <Stat k="Denominazione mancante" v={simStats.invalid} />
            <Stat k="Rag.soc. ricavata" v={simStats.derived_company} />
            <Stat k="Anagrafiche interne" v={simStats.internal} />
            <Stat k="P.IVA 10 cifre" v={simStats.vat_10_digits} />
            <Stat k="P.IVA anomale" v={simStats.vat_anomalous} />
            <Stat k="Email non valide" v={simStats.invalid_email} />
            <Stat k="PEC non valide" v={simStats.invalid_pec} />
            <Stat k="CAP da verificare" v={simStats.cap_verify} />
            <Stat k="SDI da verificare" v={simStats.sdi_verify} />
            <Stat k="Codici da generare" v={simStats.codes_to_generate} />
            <Stat k="Ignorati" v={simStats.skipped} />
          </CardContent>
          <CardContent className="flex flex-wrap justify-end gap-2 pt-0">
            <Button variant="outline" onClick={()=>setStep(1)}>Torna all'anteprima</Button>
            <Button data-testid="ci-confirm" onClick={confirm} disabled={busy} className="ama-btn-primary"><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma importazione</Button>
          </CardContent>
        </Card>
      )}

      {step === 3 && report && (
        <Card className="ama-card border-0 shadow-none" data-testid="ci-report">
          <CardHeader><CardTitle className="font-heading text-lg">Importazione completata</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat k="Creati" v={report.counts?.created || 0} />
              <Stat k="Aggiornati" v={report.counts?.updated || 0} />
              <Stat k="Invariati" v={report.counts?.unchanged || 0} />
              <Stat k="Ignorati" v={report.counts?.skipped || 0} />
              <Stat k="Falliti" v={report.counts?.failed || 0} />
              <Stat k="Batch" v={<span className="font-mono text-xs">{report.id?.slice(0,8)}</span>} />
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={async ()=>{
                const r = await api.get(`/customers/import/history/${report.id}/report.csv`, { responseType: "blob" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([r.data], { type:"text/csv" })); a.download = `import_clienti_${report.id.slice(0,8)}.csv`; a.click();
              }}><Download className="w-4 h-4 mr-1" /> Scarica rapporto CSV</Button>
              <Button onClick={()=>nav("/clienti")} className="ama-btn-primary">Torna ai clienti</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="p-3 rounded-lg bg-white border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-lg font-heading font-bold text-[#0a0a2e] break-words">{v}</div>
    </div>
  );
}
